import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Filter, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { InsightCard } from '@/components/feed/InsightCard'
import { SearchBar } from '@/components/feed/SearchBar'
import { SentimentFilter } from '@/components/feed/SentimentFilter'
import { DateDivider } from '@/components/feed/DateDivider'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { isSameDay } from '@/lib/utils/dateLabel'
import type { Insight } from '@/lib/repositories/interfaces/IInsightsRepository'

interface FeedContainerProps {
  username: string
  displayName?: string
  isAdmin: boolean
  insights: Insight[]
  trackedIds?: string[]
  trackCounts?: Record<string, number>
  isTrackLoading?: boolean
  // Controlled filter props
  searchQuery: string
  onSearchChange: (value: string) => void
  sentimentFilter: 'all' | 'bullish' | 'neutral' | 'bearish'
  onSentimentChange: (value: 'all' | 'bullish' | 'neutral' | 'bearish') => void
  // Pagination props
  hasNextPage: boolean | undefined
  isFetchingNextPage: boolean
  fetchNextPage: () => void
  // Callbacks
  onInsightClick: (
    insightId: string,
    ticker: string | undefined,
    sentiment: string,
    content: string
  ) => void
  onImageClick: (imageUrl: string) => void
  onUserClick: (userId: string, username: string, displayName?: string) => void
  onGeneralChatClick: () => void
  onEditInsight?: (insight: Insight) => void
  onDeleteInsight?: (insightId: string) => void
  onTrackToggle?: (insightId: string, isTracked: boolean) => void
}

export function FeedContainer({
  username,
  displayName,
  isAdmin,
  insights,
  trackedIds = [],
  trackCounts = {},
  isTrackLoading = false,
  searchQuery,
  onSearchChange,
  sentimentFilter,
  onSentimentChange,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  onInsightClick,
  onImageClick,
  onUserClick,
  onGeneralChatClick,
  onEditInsight,
  onDeleteInsight,
  onTrackToggle,
}: FeedContainerProps) {
  const { t } = useTranslation()
  const [showFilters, setShowFilters] = useState(false)

  const sentinelRef = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery.trim() !== '' || sentimentFilter !== 'all'

  // Clear all filters
  const clearAllFilters = () => {
    onSearchChange('')
    onSentimentChange('all')
  }

  return (
    <main className="max-w-2xl mx-auto px-4 py-6 relative z-10">
      <div className="space-y-6">
        {/* Welcome Message */}
        <div className="text-center">
          <h2
            className="text-2xl text-white mb-2"
            style={{ fontFamily: "'BDO Grotesk', sans-serif", fontWeight: 400 }}
          >
            {t('tradingIntelligenceFeed')}
          </h2>
          <p className="text-neutral-400 text-sm">
            {t('welcomeBack')}{' '}
            <span className="text-white">{displayName || username}</span>!
            {isAdmin && (
              <span className="ml-2 text-emerald-400">✨ {t('admin')}</span>
            )}
          </p>
        </div>

        {/* Search & Filter Section */}
        <div className="space-y-4">
          {/* Search Bar with Filter Toggle */}
          <div className="flex gap-2">
            <div className="flex-1">
              <SearchBar
                value={searchQuery}
                onChange={onSearchChange}
                placeholder={t('searchPlaceholder')}
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
              className={`bg-neutral-900/50 border-white/[0.08] hover:bg-neutral-800 transition-all ${
                showFilters || hasActiveFilters
                  ? 'text-emerald-400 border-emerald-500/50'
                  : 'text-neutral-400'
              }`}
            >
              <Filter className="w-5 h-5" />
            </Button>
          </div>

          {/* Expandable Filters */}
          {showFilters && (
            <div className="animate-in slide-in-from-top-4 duration-300">
              {/* Sentiment Filter */}
              <div>
                <label className="text-sm font-medium text-neutral-400 mb-2 block">
                  {t('sentiment')}
                </label>
                <SentimentFilter
                  value={sentimentFilter}
                  onChange={onSentimentChange}
                />
              </div>
            </div>
          )}

          {/* Active Filters Indicator */}
          {hasActiveFilters && (
            <div className="flex items-center justify-between py-2 px-4 bg-neutral-900/30 rounded-lg border border-white/[0.06] animate-in fade-in slide-in-from-top-2 duration-200">
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-400">
                  {t('filterActive')}
                </span>
              </div>
              <button
                onClick={clearAllFilters}
                className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
              >
                {t('clearAll')}
              </button>
            </div>
          )}
        </div>

        {/* Insights Feed */}
        {insights.length > 0 ? (
          <div className="space-y-4">
            {insights.map((insight, i) => {
              const prevDate =
                i > 0 ? new Date(insights[i - 1]!.createdAt) : null
              const currentDate = new Date(insight.createdAt)
              const showDivider =
                i === 0 || (prevDate && !isSameDay(prevDate, currentDate))

              return (
                <Fragment key={insight.id}>
                  {showDivider && <DateDivider date={insight.createdAt} />}
                  <InsightCard
                    insight={insight}
                    isCurrentUserAdmin={isAdmin}
                    isTracked={trackedIds.includes(insight.id)}
                    trackCount={trackCounts[insight.id] || 0}
                    isTrackLoading={isTrackLoading}
                    onInsightClick={onInsightClick}
                    onImageClick={onImageClick}
                    onUserClick={onUserClick}
                    onEdit={onEditInsight}
                    onDelete={onDeleteInsight}
                    onTrackToggle={onTrackToggle}
                  />
                </Fragment>
              )
            })}

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-1" />

            {/* Loading more indicator */}
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
              </div>
            )}

            {/* End of Results Message */}
            {!hasNextPage && (
              <div className="text-center py-8">
                <p className="text-neutral-500 text-sm">
                  {t('endOfFeed')}
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16 animate-in fade-in zoom-in duration-300">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-neutral-900/50 flex items-center justify-center border border-white/[0.06]">
              <Filter className="w-8 h-8 text-neutral-500" />
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">
              {t('noInsightsFound')}
            </h3>
            <p className="text-neutral-400 text-sm mb-6 max-w-sm mx-auto">
              {searchQuery
                ? t('noInsightsMatchQuery', { query: searchQuery })
                : t('adjustFilters')}
            </p>
            {hasActiveFilters && (
              <Button
                onClick={clearAllFilters}
                variant="outline"
                className="bg-neutral-900/50 border-white/[0.08] text-neutral-300 hover:bg-neutral-800 hover:text-white"
              >
                {t('clearAllFilters')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Floating AI Chat Button - All Users */}
      <button
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center transition-all hover:scale-110 active:scale-95 z-20"
        title={t('askSovereign')}
        onClick={onGeneralChatClick}
      >
        <Sparkles className="w-6 h-6 text-black" />
      </button>
    </main>
  )
}
